import React, {useCallback, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import {MuiRFF, Yup} from '../../form';
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import useAccessLevels from "../../../hooks/use-access-levels";


const formNames = {
    description: "description",
}

const schema = Yup.object().shape({
    [formNames.description]: Yup.string().nullable().trim().required(ValidateMessages.required),
});


const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const SuggestTechnicalPropertyDialog = ({industryId, open, close}) => {
    const {packages} = useAccessLevels();
    const canSubmitTechnicalSuggestions = packages.lookup.submitTechnicalSuggestions;
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Submits a new technical property suggestion for the current product:
     */
    const suggestNewProperty = useCallback(async (values) => {
        if (!packages.lookup.submitTechnicalSuggestions) {
            return;
        }
        setSubmitting(true);
        const forApi = {
            industryId: industryId,
            description: values[formNames.description],
        }
        const response = await Api.suggestIndustryProperty(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            close();
        }
    }, [isMounted, close, industryId, packages.lookup.submitTechnicalSuggestions])

    if (!canSubmitTechnicalSuggestions) {
        return null;
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={'sm'}
                loading={submitting}
                disableEscapeKeyDown={submitting}
                disableBackdropClick={submitting}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Suggest a new standard property
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2'}>
                    <Col xs={12} className={'mb-4 mt-4'}>
                        <p className={'text-sm font-weight-regular mt-2 mb-2 part-number-fluid'}>
                            Please describe the property that you want to add to the standard list of properties, we will check your request
                            and get back to you.
                        </p>
                    </Col>
                    <Col xs={12} className={'mb-4'}>
                        <Form
                            className='d-flex flex-wrap w-100 align-items-start'
                            onSubmit={suggestNewProperty}
                            id={'form-id'}
                            validate={validate}
                            render={() => {
                                return (
                                    <>
                                        <Col xs={12} className={'p-0 mb-4'}>
                                            <MuiRFF.TextField
                                                multiline
                                                minRows={3}
                                                name={formNames.description}
                                                label={'Description'}
                                                required={required[formNames.description]}
                                                disabled={submitting}
                                            />
                                        </Col>
                                    </>
                                )
                            }}
                        />
                    </Col>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => close()}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'form-id'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {
                                    submitting
                                        ? "Submitting..."
                                        : "Submit"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default SuggestTechnicalPropertyDialog;
